import { Box, Typography } from '@mui/material';
import { QrCode2 as QrCode2Icon } from '@mui/icons-material';

// Pedagogisk "bild" av hela PDF-fakturan (fiktiva uppgifter: Test1/Test2 Testsson, 888/111 kr). Används i Texter-steget
// för att visa VAR valen hamnar: highlight='text' ramar in fakturatexten, highlight='slip' ramar in inbetalningskortet.
// Detta är en förenklad återgivning – den exakta PDF:en hämtas separat via "Visa exakt PDF" (renderas av API:et).
interface SendingInvoiceMockProps {
  formTemplate: string;
  invoiceHeading?: string;
  invoiceText?: string;
  accountNr?: string;
  senderName?: string;
  highlight?: 'text' | 'slip' | null;
  width?: number;
  // true = föreningens egna logga vald. Placeholdern visar alltid förbundets logga; en info-not upplyser om att
  // logotypen byts till medlemmens förenings logga vid det skarpa utskicket.
  useLocalAssLogo?: boolean;
  // Förbundets (centrala) logga som bild-URL/data-URI. Visas i sidhuvudet när förbundets logga är vald.
  logoSrc?: string;
}

// Markerings-stil för det området ett val påverkar (blå ram + svag glow) samt en liten etikett-chip ovanför.
const highlightSx = {
  outline: '2px solid',
  outlineColor: 'primary.main',
  outlineOffset: 2,
  borderRadius: 1,
  boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.12)',
  position: 'relative' as const,
};

const HighlightLabel = ({ text }: { text: string }) => (
  <Box
    sx={{
      position: 'absolute',
      top: -9,
      right: 6,
      bgcolor: 'primary.main',
      color: '#fff',
      px: 0.9,
      py: 0.2,
      borderRadius: 1,
      fontSize: '0.66rem',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      zIndex: 2,
    }}
  >
    {text}
  </Box>
);

export const SendingInvoiceMock = ({
  formTemplate,
  invoiceHeading,
  invoiceText,
  accountNr,
  senderName,
  highlight = null,
  width = 300,
  useLocalAssLogo = false,
  logoSrc = '',
}: SendingInvoiceMockProps) => {
  // Visa den riktiga loggan så snart en sådan skickats in (logoSrc): förbundets logga i förbundsläge, eller den
  // förhandsgranskade föreningens logga i föreningsläge. Saknas logga (föreningsläge utan vald förening) visar vi
  // placeholdern + info-not, eftersom loggan då byts per medlem vid det skarpa utskicket.
  const showRealLogo = !!logoSrc;
  const isBlank = formTemplate === 'blank';
  const isPg = formTemplate === 'pgocr';
  const accountDisplay = accountNr || '376-6938';
  const giroDigits = accountDisplay.replace(/\D/g, '') || '3766938';
  const ocrRef = '123456789';
  const receiver = senderName || 'Betalningsmottagare';
  // Fiktiv betalare/mottagare – samma uppgifter i fakturans mottagarrad som på inbetalningskortet.
  const payerName = 'Test Testsson';
  const payerAddr = 'Kastellvägen 2A';
  const payerCity = '825 40 Hudiksvall';

  // Nederkantens inbetalningskort – Bankgiro, Plusgiro eller ingen avi.
  let slip;
  if (isBlank) {
    slip = (
      <Box sx={{ border: '1px dashed', borderColor: 'grey.400', borderRadius: 0.5, p: 1.25, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.5rem', color: 'text.secondary' }}>
          Inget inbetalningskort. Fakturan skrivs ut utan avi – betalning sker via gruppens aktiva betalsätt.
        </Typography>
      </Box>
    );
  } else if (isPg) {
    slip = (
      <Box sx={{ border: 1, borderColor: 'grey.700', position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.75, py: 0.4, borderBottom: 1, borderColor: 'grey.400' }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: '0.6rem' }}>PlusGirot</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '0.45rem' }}>INBETALNING / GIRERING</Typography>
        </Box>
        <Typography
          sx={{
            position: 'absolute',
            right: 6,
            top: 44,
            fontSize: '1.8rem',
            fontWeight: 300,
            color: 'grey.300',
            lineHeight: 1,
            pointerEvents: 'none',
          }}
        >
          C
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, p: 0.75 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.42rem', lineHeight: 1.3 }}>Betalningsreferens: {ocrRef}</Typography>
            <Typography sx={{ fontSize: '0.42rem' }}>IBAN SE8480000832799140755993</Typography>
            <Typography sx={{ fontSize: '0.42rem' }}>SWIFT-BIC SWEDSESS</Typography>
            <QrCode2Icon sx={{ fontSize: 34, color: 'grey.900', mt: 0.25 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            <Box sx={{ border: 1, borderColor: 'grey.400', px: 0.5, py: 0.15 }}>
              <Typography sx={{ fontSize: '0.38rem', color: 'text.secondary' }}>Till PlusGirokonto</Typography>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700 }}>{accountDisplay}</Typography>
            </Box>
            <Box sx={{ border: 1, borderColor: 'grey.400', px: 0.5, py: 0.15 }}>
              <Typography sx={{ fontSize: '0.38rem', color: 'text.secondary' }}>Betalningsmottagare (endast namn)</Typography>
              <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {receiver}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ px: 0.75, py: 0.4, borderTop: 1, borderColor: 'grey.400', overflowX: 'auto' }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.5rem', whiteSpace: 'nowrap' }}>
            {`#  ${ocrRef} #  999 00  3 >  ${giroDigits}#14#`}
          </Typography>
        </Box>
      </Box>
    );
  } else {
    slip = (
      <Box sx={{ border: 1, borderColor: 'grey.700' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.75, p: 0.75, borderBottom: 1, borderColor: 'grey.400' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontStyle: 'italic', fontSize: '0.6rem', lineHeight: 1 }}>bankgirot</Typography>
            <Typography sx={{ fontSize: '0.4rem', color: 'text.secondary', mt: 0.25 }}>Med denna avi kan du betala på alla bankkontor.</Typography>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.45rem' }}>INBETALNING/GIRERING AVI</Typography>
            <Box sx={{ border: 1, borderColor: 'grey.800', px: 0.75, mt: 0.25, display: 'inline-block' }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', letterSpacing: 1 }}>OCR</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, p: 0.75 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <QrCode2Icon sx={{ fontSize: 34, color: 'grey.900' }} />
            <Typography sx={{ fontSize: '0.42rem' }}>Betalningsreferens: {ocrRef}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pt: 1 }}>
            <Typography sx={{ fontSize: '0.45rem', fontWeight: 600 }}>{payerName}</Typography>
            <Typography sx={{ fontSize: '0.45rem' }}>{payerCity}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', borderTop: 1, borderColor: 'grey.400' }}>
          <Box sx={{ flex: 1, px: 0.75, py: 0.3, borderRight: 1, borderColor: 'grey.400', minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.38rem', color: 'text.secondary' }}>TILL BANKGIRO NR</Typography>
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 700 }}>{accountDisplay}</Typography>
          </Box>
          <Box sx={{ flex: 2, px: 0.75, py: 0.3, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.38rem', color: 'text.secondary' }}>BETALNINGSMOTTAGARE</Typography>
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {receiver}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ px: 0.75, py: 0.4, borderTop: 1, borderColor: 'grey.400', overflowX: 'auto' }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.5rem', whiteSpace: 'nowrap' }}>
            {`H #  ${ocrRef} #  600 00  7 >  ${giroDigits}#41#`}
          </Typography>
        </Box>
      </Box>
    );
  }

  let slipLabel = 'Inget inbetalningskort';
  if (!isBlank) slipLabel = isPg ? 'Vald blankett: Plusgiro OCR' : 'Vald blankett: Bankgiro OCR';

  return (
    // Hela "sidan" – vit yta med A4-proportioner (210:297) oavsett innehåll, precis som PDF:en. Sidfot + inbetalningskort
    // ligger pinnade i nederkant; överflödande innehåll klipps (overflow hidden) så formatet alltid ser ut som ett A4.
    <Box
      sx={{
        width,
        maxWidth: '100%',
        aspectRatio: '210 / 297',
        mx: 'auto',
        bgcolor: '#fff',
        border: 1,
        borderColor: 'grey.300',
        boxShadow: 2,
        p: 1.5,
        color: 'grey.900',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Sidhuvud: logo-placeholder + referens + datum (kompakt, låg höjd) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Box
          sx={{
            width: 56,
            height: 24,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            ...(showRealLogo ? {} : { border: '1px dashed', borderColor: 'grey.400', borderRadius: 0.5 }),
          }}
        >
          {showRealLogo ? (
            <Box component='img' src={logoSrc} alt='Logotyp' sx={{ maxWidth: '100%', maxHeight: '100%' }} />
          ) : (
            <Typography sx={{ fontSize: '0.4rem', color: 'text.disabled', letterSpacing: 0.5 }}>LOGO</Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '0.4rem', fontWeight: 700 }}>Betalningsreferens/OCR</Typography>
          <Typography sx={{ fontSize: '0.45rem' }}>{ocrRef}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '0.4rem' }}>
            <b>Utskrift</b> 2026-09-01
          </Typography>
          <Typography sx={{ fontSize: '0.4rem' }}>
            <b>Betalas senast</b> 2026-10-01
          </Typography>
        </Box>
      </Box>

      {/* Föreningens egna logga vald: antingen förhandsgranskar vi en specifik förenings logga (showRealLogo), eller
          så visar vi placeholdern. Oavsett vilket byts loggan per medlem vid det skarpa utskicket – info-not så kunden
          förstår skillnaden. */}
      {useLocalAssLogo && (
        <Typography sx={{ mt: 0.4, fontSize: '0.38rem', fontStyle: 'italic', color: 'primary.main' }}>
          {showRealLogo
            ? 'Förhandsgranskad förenings logga. Vid utskicket används varje medlems förenings logga.'
            : 'Logotypen byts till medlemmens förenings logga vid utskicket.'}
        </Typography>
      )}

      {/* Mottagare (en rad) */}
      <Typography sx={{ mt: 0.75, textAlign: 'right', fontSize: '0.42rem' }}>
        {payerName} · {payerAddr} · {payerCity}
      </Typography>

      {/* Rubrik ovanför artikelraderna (invoiceHeading) – markeras vid textredigering */}
      <Box sx={{ mt: 1, mb: 0.25, ...(highlight === 'text' ? highlightSx : {}) }}>
        {highlight === 'text' && <HighlightLabel text='Din rubrik' />}
        <Typography
          sx={{
            fontSize: '0.5rem',
            fontWeight: 700,
            borderBottom: 1,
            borderColor: 'grey.300',
            pb: 0.25,
            color: invoiceHeading ? 'inherit' : 'text.disabled',
          }}
        >
          {invoiceHeading || 'Ingen rubrik angiven'}
        </Typography>
      </Box>

      {/* Fakturarader (fiktiva) */}
      {[
        { nr: '11111', name: 'Test1 Testsson1', text: 'Enskilt medlemskap', amount: '888 kr' },
        { nr: '22222', name: 'Test2 Testsson2', text: 'Familjemedlem', amount: '111 kr' },
      ].map((r) => (
        <Box key={r.nr} sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, py: 0.2, borderBottom: 1, borderColor: 'grey.100' }}>
          <Typography sx={{ fontSize: '0.45rem', color: 'text.secondary', width: 28 }}>{r.nr}</Typography>
          <Typography sx={{ fontSize: '0.45rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</Typography>
          <Typography sx={{ fontSize: '0.45rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.text}</Typography>
          <Typography sx={{ fontSize: '0.45rem', fontWeight: 600 }}>{r.amount}</Typography>
        </Box>
      ))}

      {/* Fritext UNDER raderna (invoiceText) – markeras vid textredigering */}
      <Box sx={{ mt: 1, mb: 1.5, minHeight: 26, ...(highlight === 'text' ? highlightSx : {}) }}>
        {highlight === 'text' && <HighlightLabel text='Din fakturatext hamnar här' />}
        {invoiceText ? (
          // eslint-disable-next-line react/no-danger
          <Typography
            // Kundens text kan bära egen CSS (t.ex. ett PÅMINNELSE-vattenmärke med position: fixed). contain: paint
            // gör den här rutan till containing block för sådana element, så de stannar i förhandsvisningen i
            // stället för att lägga sig över hela sidan och blockera klick.
            sx={{ fontSize: '0.32rem', lineHeight: 1.25, color: 'text.secondary', contain: 'paint', position: 'relative', overflow: 'hidden' }}
            component='div'
            dangerouslySetInnerHTML={{ __html: invoiceText }}
          />
        ) : (
          <Typography sx={{ fontSize: '0.45rem', color: 'text.disabled' }}>Ingen text angiven</Typography>
        )}
      </Box>

      {/* Sidfot + inbetalningskort pinnade i nederkant (mt:auto) så sidan alltid har A4-format med avin längst ner. */}
      <Box sx={{ mt: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, borderTop: 1, borderColor: 'grey.300', pt: 0.4, mb: 1 }}>
          <Typography sx={{ fontSize: '0.4rem', color: 'text.secondary' }}>Tel: 010-123456</Typography>
          <Typography sx={{ fontSize: '0.4rem', color: 'text.secondary' }}>
            {isPg ? 'Plusgiro' : 'Bankgiro'}: {accountDisplay}
          </Typography>
          <Typography sx={{ fontSize: '0.4rem', color: 'text.secondary' }}>Org nr: 123456-7890</Typography>
        </Box>

        {/* Inbetalningskort – markeras vid blankettvalet */}
        <Box sx={{ ...(highlight === 'slip' ? highlightSx : {}) }}>
          {highlight === 'slip' && <HighlightLabel text={slipLabel} />}
          {slip}
        </Box>
      </Box>
    </Box>
  );
};
